import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import { Dialog, IconButton } from "@mui/material";
import {
  DialogContentTemplate as DialogTemplate,
  DialogTemplateProps,
} from "@benefit-systems/hr-tools-ui";
import CloseIcon from "@mui/icons-material/Close";

interface IGlobalModal {
  /** Optional owner id, lets the opener update its own modal later (e.g. disable actions). */
  id?: string;
  open: boolean;
  title: string | ReactNode;
  description?: string | ReactNode;
  maxWidth?: number;
  disableExitButton?: boolean;
  disableOutsideClick?: boolean;
}

type GlobalModal = IGlobalModal & DialogTemplateProps;

interface IGlobalModalContext {
  modal: GlobalModal;
  setModal: Dispatch<SetStateAction<GlobalModal>>;
  resetModal: VoidFunction;
}

const defaultValue: GlobalModal = {
  open: false,
  title: "",
  description: "",
  actions: [],
  maxWidth: 600,
  disableExitButton: false,
  disableOutsideClick: false,
};

export const GlobalModalContext = createContext<IGlobalModalContext>({
  modal: defaultValue,
  setModal: () => {},
  resetModal: () => {},
});

export const GlobalModalContextProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [modal, setModal] = useState<GlobalModal>(defaultValue);

  const resetModal = useCallback(() => {
    setModal((prevState) => ({
      ...prevState,
      open: false,
    }));
  }, []);

  const {
    open,
    title,
    description,
    customElements,
    disableExitButton,
    disableOutsideClick,
    maxWidth,
    actions,
  } = modal;

  return (
    <GlobalModalContext.Provider value={{ modal, setModal, resetModal }}>
      {children}
      {modal && (
        <Dialog
          open={open}
          scroll="body"
          fullWidth
          maxWidth={false}
          data-testid="os__global-modal"
          onClose={(event, reason) => {
            if (
              disableOutsideClick &&
              (reason === "backdropClick" || reason === "escapeKeyDown")
            ) {
              return;
            }
            resetModal();
          }}
          sx={{
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: maxWidth ?? 800,
              padding: 4,
              borderRadius: 2,
              boxSizing: "border-box",
              "@media (max-width: 900px)": {
                margin: 2,
                maxWidth: "90vw",
              },
            },
          }}
        >
          {!disableExitButton && (
            <IconButton
              aria-label="close"
              onClick={resetModal}
              sx={{
                position: "absolute",
                right: "1.25rem",
                top: "1.25rem",
                fontSize: "2rem",
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <DialogTemplate
            title={title as string}
            description={description}
            actions={actions}
            customElements={customElements}
          />
        </Dialog>
      )}
    </GlobalModalContext.Provider>
  );
};

export const useGlobalModal = () => {
  const context = useContext(GlobalModalContext);

  if (context === undefined) {
    throw new Error("GlobalModalContext was used outside of its Provider");
  }

  return context;
};
